import { Button } from '@televet/kibble-ui/build/components/Button';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Radio } from '@televet/kibble-ui/build/components/Radio';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { Link } from '@televet/kibble-ui/build/components/Link';
import { Box, Flex, Stack } from '@televet/kibble-ui/build/chakra';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { RouteDefinitions, SettingsRoutes } from 'routes';
import { useValidation } from 'shared/hooks';
import useClinicUser from 'shared/hooks/useClinicUser';
import { EmailCampaignFragment, EmailCampaignStatus, useUpdateOneEmailCampaignMutation } from 'shared/types/graphql';

type ReplyToProps = {
  campaign?: EmailCampaignFragment;
  refetchEmailCampaigns: () => void;
};

const ReplyTo = ({ campaign, refetchEmailCampaigns }: ReplyToProps): JSX.Element => {
  const [isEditReplyTo, setIsEditReplyTo] = useState(false);
  const [isAcceptingReplies, setIsAcceptingReplies] = useState(!!campaign?.replyToJson?.email);
  const { currentClinic } = useClinicUser();

  const [needsUserAttention, setNeedsUserAttention] = useState(campaign?.replyToJson?.needsUserAttention);

  const [replyToDraft, setReplyToDraft] = useState(campaign?.replyToJson?.email || currentClinic?.clinicEmail || '');
  const [replyToValidationObject, setReplyToValidationObject] = useState({
    isValid: true,
    errorMessage: '',
  });

  const toast = useToast();

  const [updateCampaign, { loading: updateCampaignLoading }] = useUpdateOneEmailCampaignMutation();

  const { validateEmail } = useValidation();

  useEffect(() => {
    if (currentClinic?.clinicEmail && needsUserAttention) {
      updateCampaign({
        variables: {
          data: {
            replyToJson: { email: currentClinic.clinicEmail, needsUserAttention: false },
          },
          where: {
            id: campaign?.id,
          },
        },
      }).then(() => {
        refetchEmailCampaigns();
        setNeedsUserAttention(false);
        setReplyToDraft(currentClinic.clinicEmail);
      });
    }
  }, [campaign?.id, currentClinic?.clinicEmail, needsUserAttention, refetchEmailCampaigns, updateCampaign]);

  const handleReplyToValidation = (email: string): { isValid: boolean; errorMessage: string } => {
    const { isValid } = validateEmail(email);
    let errorMessage = '';
    if (!isValid) {
      errorMessage = 'Please add a valid email.';
    }
    setReplyToValidationObject({ isValid, errorMessage });
    return { isValid, errorMessage };
  };
  const handleToggleAcceptingReplies = (resetEmail?: boolean): void => {
    if (resetEmail) {
      setReplyToDraft('');
      setReplyToValidationObject({ isValid: true, errorMessage: '' });
    } else {
      setReplyToDraft(replyToDraft || currentClinic?.clinicEmail || '');
    }

    setIsAcceptingReplies((prevState) => !prevState);
  };
  const handleSaveReplyTo = async (): Promise<void> => {
    try {
      const { isValid } = handleReplyToValidation(replyToDraft);
      if (isValid || (replyToDraft === '' && !isAcceptingReplies)) {
        setReplyToValidationObject({ isValid: true, errorMessage: '' });
        await updateCampaign({
          variables: {
            data: {
              replyToJson: { email: replyToDraft, needsUserAttention: false },
            },
            where: {
              id: campaign?.id,
            },
          },
        });
        setIsEditReplyTo(false);
        setNeedsUserAttention(false);
      }
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'There was an error saving the reply to email.',
        status: 'error',
      });
    }
  };
  const getReplyToCopy = (): React.ReactElement => {
    if (needsUserAttention) {
      return (
        <Text as="p" fontWeight="normal" color="text.subtle">
          If a client replies to your email, they will get an automatic response directing them to contact your clinic
          with any questions.{' '}
          <Link as={NavLink} to={`${RouteDefinitions.Settings}${SettingsRoutes.ClinicGeneral}`}>
            General Info
          </Link>
          .
        </Text>
      );
    } else if (campaign?.replyToJson?.email) {
      return (
        <Text as="p" fontWeight="normal" color="text.subtle">
          Replies will be directed to: <Text fontWeight="bold">{campaign.replyToJson.email}</Text>
        </Text>
      );
    }
    return (
      <Text as="p" fontWeight="normal" color="text.subtle">
        If a client replies to your email, they will get an automatic response directing them to contact your clinic
        with any questions.
      </Text>
    );
  };

  return (
    <Flex justifyContent="space-between" padding="2">
      <Flex flex="1" paddingLeft="2">
        <Box
          ml="-8px"
          mr="8px"
          borderRadius="4px"
          bgColor={needsUserAttention ? 'border.warning' : 'background.light'}
          minWidth="4px"
          maxWidth="4px"
          width="4px"
        />
        <Box minWidth="24px" minHeight="24px" paddingY="2">
          {needsUserAttention ? (
            <Icon size="lg" name="alertCircle" variant="warning" />
          ) : (
            <Icon size="lg" name="checkmarkCircle" variant="success" />
          )}
        </Box>
        <Box width="100%" ml="2" paddingY="2">
          <Text as="p" fontWeight="bold" fontSize="xl">
            Reply-To
          </Text>
          {isEditReplyTo ? (
            <Box width="100%" paddingRight="3">
              <Stack width="100%" mt="3">
                <Radio
                  size="lg"
                  isChecked={!isAcceptingReplies}
                  onChange={(): void => handleToggleAcceptingReplies(true)}
                >
                  <Text as="p" fontWeight="bold">
                    Do not accept replies
                  </Text>
                  <Text as="p" fontWeight="normal" color="text.subtle">
                    If a client replies to your email, they will get an automatic response directing them to contact
                    your clinic with any questions.
                  </Text>
                </Radio>
                <Radio
                  size="lg"
                  isChecked={isAcceptingReplies}
                  onChange={(): void => handleToggleAcceptingReplies(false)}
                >
                  <Text as="p" fontWeight="bold">
                    Accept replies
                  </Text>
                  <Text as="p" fontWeight="normal" color="text.subtle">
                    Replies will be directed to the email address you choose. No automatic reply will be sent.
                  </Text>
                </Radio>
              </Stack>
              {isAcceptingReplies && (
                <Flex mt="2" mb="4">
                  <TextInput
                    label="What email would you like to receive replies to?"
                    autoFocus
                    width="100%"
                    value={replyToDraft}
                    isInvalid={!replyToValidationObject.isValid}
                    errorText={replyToValidationObject.errorMessage}
                    onChange={(e): void => {
                      setReplyToDraft(e.target.value);
                    }}
                  />
                </Flex>
              )}
              <Flex justifyContent="flex-end">
                <Button
                  variant="tertiary"
                  mr="4"
                  onClick={(): void => {
                    // setSubjectDraft(subject);
                    // handleSubjectValidation(subject);
                    setIsEditReplyTo(false);
                  }}
                >
                  Cancel
                </Button>
                <Button isLoading={updateCampaignLoading} onClick={handleSaveReplyTo}>
                  Save
                </Button>
              </Flex>
            </Box>
          ) : (
            <>{getReplyToCopy()}</>
          )}
        </Box>
      </Flex>
      {!isEditReplyTo && campaign?.status === EmailCampaignStatus.Draft && (
        <Button
          margin="3"
          onClick={(): void => {
            setIsEditReplyTo(true);
            if (campaign.replyToJson.email) {
              setIsAcceptingReplies(true);
            } else {
              setIsAcceptingReplies(false);
            }
          }}
          variant="secondary"
        >
          {campaign.replyToJson?.email ? 'Edit' : 'Add'} Reply-To
        </Button>
      )}
    </Flex>
  );
};

export default ReplyTo;
